import React, { useEffect, useState } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Download, Calendar } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AnalyticsAPI, RevenueAnalytics } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';

export default function RevenueAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<RevenueAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');

  useEffect(() => {
    fetchAnalytics();
  }, [period, groupBy]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AnalyticsAPI.getRevenueAnalytics({ period, groupBy });
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching revenue analytics:', err);
      setError(err.message || 'Failed to load revenue analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading revenue analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Failed to load analytics'}</p>
          <Button onClick={fetchAnalytics}>Retry</Button>
        </div>
      </div>
    );
  }

  const { data } = analytics;
  const avgRevenue = data.trends.length > 0 
    ? data.trends.reduce((sum, t) => sum + t.revenue, 0) / data.trends.length 
    : 0;
  const maxRevenue = data.trends.length > 0 
    ? Math.max(...data.trends.map(t => t.revenue)) 
    : 0;
  const minRevenue = data.trends.length > 0 
    ? Math.min(...data.trends.map(t => t.revenue)) 
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Revenue Analytics</h1>
          <p className="text-muted-foreground">Detailed revenue insights and trends</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <select 
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as any)}
          >
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </select>
          <select 
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value as any)}
          >
            <option value="day">Last 24 hours</option>
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
            <option value="year">Last year</option>
          </select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">{formatCurrency(data.totalRevenue)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Average Revenue</p>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">{formatCurrency(avgRevenue)}</p>
            <p className="text-xs text-muted-foreground mt-1">Per {groupBy}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Peak Revenue</p>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-2xl font-bold">{formatCurrency(maxRevenue)}</p>
            <p className="text-xs text-muted-foreground mt-1">Highest {groupBy}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Lowest Revenue</p>
              <TrendingDown className="h-4 w-4 text-red-600" />
            </div>
            <p className="text-2xl font-bold">{formatCurrency(minRevenue)}</p>
            <p className="text-xs text-muted-foreground mt-1">Lowest {groupBy}</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue chart */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue Trends</CardTitle>
          <CardDescription>
            {groupBy.charAt(0).toUpperCase() + groupBy.slice(1)} revenue breakdown
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[400px]">
            {data.trends.length > 0 ? (
              <div className="flex flex-col h-full">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex gap-4">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-primary mr-2"></div>
                      <span className="text-sm">Revenue</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">{formatCurrency(data.totalRevenue)}</div>
                    <div className="text-xs text-muted-foreground">Total Revenue</div>
                  </div>
                </div>
                
                <div className="flex-1 flex items-end gap-1">
                  {data.trends.map((trend) => {
                    const height = maxRevenue > 0 ? (trend.revenue / maxRevenue) * 100 : 0;
                    const date = new Date(trend.date);
                    const label = groupBy === 'day' 
                      ? `${date.getMonth() + 1}/${date.getDate()}`
                      : groupBy === 'week'
                      ? `W${Math.ceil(date.getDate() / 7)}`
                      : date.toLocaleDateString('en-US', { month: 'short' });
                    
                    return (
                      <div key={trend.date} className="relative flex-1 flex flex-col items-center group h-[300px]">
                        <div
                          className="w-full bg-primary rounded-t-sm transition-all hover:opacity-80"
                          style={{ height: `${height}%` }}
                        ></div>
                        <span className="mt-2 text-xs truncate w-full text-center">{label}</span>
                        <div className="absolute -top-20 hidden group-hover:block bg-popover text-popover-foreground px-3 py-2 rounded text-xs whitespace-nowrap shadow-lg border z-10">
                          <div className="font-medium">{new Date(trend.date).toLocaleDateString()}</div>
                          <div className="text-primary">{formatCurrency(trend.revenue)}</div>
                          <div className="text-muted-foreground">{trend.orderCount} orders</div>
                          <div className="text-muted-foreground">Avg: {formatCurrency(trend.avgOrderValue)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No revenue data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Detailed table */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue Details</CardTitle>
          <CardDescription>Detailed breakdown by period</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 font-medium">Date</th>
                  <th className="text-right p-2 font-medium">Revenue</th>
                  <th className="text-right p-2 font-medium">Orders</th>
                  <th className="text-right p-2 font-medium">Avg Order Value</th>
                </tr>
              </thead>
              <tbody>
                {data.trends.map((trend) => (
                  <tr key={trend.date} className="border-b hover:bg-muted/50">
                    <td className="p-2">{new Date(trend.date).toLocaleDateString()}</td>
                    <td className="p-2 text-right font-medium">{formatCurrency(trend.revenue)}</td>
                    <td className="p-2 text-right">{trend.orderCount}</td>
                    <td className="p-2 text-right">{formatCurrency(trend.avgOrderValue)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold">
                  <td className="p-2">Total</td>
                  <td className="p-2 text-right">{formatCurrency(data.totalRevenue)}</td>
                  <td className="p-2 text-right">
                    {data.trends.reduce((sum, t) => sum + t.orderCount, 0)}
                  </td>
                  <td className="p-2 text-right">{formatCurrency(avgRevenue)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
